/**
 * TrendBadge — compact ▲/▼ pill showing % change + timeframe.
 * Emerald when up, rose when down, muted when flat.
 *
 * Used anywhere a big static number needs a glanceable direction indicator.
 */
import { TrendingUp, TrendingDown, Minus } from 'lucide-react'
import { clsx } from 'clsx'

interface Props {
  pct: number | null | undefined
  label?: string        // e.g. "30d", "YTD"
  size?: 'xs' | 'sm' | 'md'
  inverted?: boolean    // set true when "up is bad" (inflation, unemployment)
  className?: string
}

export default function TrendBadge({
  pct, label, size = 'sm', inverted = false, className,
}: Props) {
  if (pct === null || pct === undefined || Number.isNaN(pct)) return null

  const isFlat = Math.abs(pct) < 0.05
  const up = pct >= 0
  const isGood = inverted ? !up : up

  const sizeClass = {
    xs: 'text-[9px] gap-0.5 px-1 py-0',
    sm: 'text-[10px] gap-0.5 px-1.5 py-0.5',
    md: 'text-xs gap-1 px-2 py-0.5',
  }[size]

  const colour = isFlat
    ? 'text-text-muted bg-slate-500/10 border-slate-500/30'
    : isGood
    ? 'text-emerald-400 bg-emerald-500/10 border-emerald-500/30'
    : 'text-rose-400 bg-rose-500/10 border-rose-500/30'

  const Icon = isFlat ? Minus : up ? TrendingUp : TrendingDown
  const iconSize = size === 'md' ? 11 : 9

  return (
    <span
      className={clsx(
        'inline-flex items-center rounded border font-mono font-semibold',
        sizeClass, colour, className,
      )}
      title={`${up ? '+' : ''}${pct.toFixed(2)}%${label ? ` over ${label}` : ''}${inverted ? ' (inverted: up = bad)' : ''}`}
    >
      <Icon size={iconSize} aria-hidden />
      <span>{up ? '+' : ''}{pct.toFixed(1)}%</span>
      {label && <span className="opacity-60 ml-0.5">{label}</span>}
    </span>
  )
}
